import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper as SwiperBase, useSwiper } from 'swiper/react';
import { Pagination, Autoplay, A11y, Grid } from 'swiper/modules';
import './swiper.scss';
import PlaySvg from '../../../icons/play.svg?react';
import PauseSvg from '../../../icons/pause.svg?react';
import useForceUpdate from '../Hooks/useForceUpdate';

function PaginationController({ autoplayControl }) {
  const swiper = useSwiper();
  const { autoplay } = swiper;
  const [running, setRunning] = useState(true);

  useEffect(() => {
    const handleAutoplayStop = () => setRunning(false);
    const handleAutoplayStart = () => setRunning(true);

    swiper.on('autoplayStop', handleAutoplayStop);
    swiper.on('autoplayStart', handleAutoplayStart);
    return () => {
      swiper.off('autoplayStop', handleAutoplayStop);
      swiper.off('autoplayStart', handleAutoplayStart);
    };
  }, [swiper]);

  const onClick = () => {
    if (running) {
      return autoplay.stop();
    } else {
      return autoplay.start();
    }
  };

  return (
    <div className='swiper-pagination'>
      {autoplayControl && (
        <button
          className={`button swiper-autoplay-button ${running ? 'swiper-autoplay-inactive' : 'swiper-autoplay-active'}`}
          type='button'
          aria-label={running ? 'pause' : 'play'}
          onClick={onClick}
        >
          {running ? <PauseSvg fill='currentColor' /> : <PlaySvg fill='currentColor' />}
        </button>
      )}
      <div className='swiper-pagination-container'></div>
    </div>
  );
}

/**
 * @typedef {Object} SwiperProps
 */

/**
 * @param {SwiperProps & import('swiper/react').SwiperOptions} props
 */
const Swiper = (props) => {
  const {
    containerClass = '',
    onSlideChange,
    children,
    pagination,
    navigation,
    autoplay,
    grid,
    onSwiper,
    ...rest
  } = props;
  const forceUpdate = useForceUpdate();
  const swiperRef = useRef(null);
  const modules = [pagination && Pagination, A11y, autoplay && Autoplay, grid && Grid].filter((m) => !!m);

  const { isBeginning, isEnd, isLocked } = swiperRef?.current || {};
  const showNavigationArrows = !isLocked && navigation && navigation?.enabled !== false;

  const isLeftDisabled = isBeginning && !props.loop;
  const isRightDisabled = isEnd && !props.loop;

  return (
    <div className={`swiper-container ${containerClass}`}>
      <SwiperBase
        modules={modules}
        {...rest}
        autoplay={autoplay}
        grid={grid}
        pagination={
          pagination ? { el: '.swiper-pagination-container', type: 'bullets', clickable: true, ...pagination } : false
        }
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          onSwiper && onSwiper(swiper);
        }}
        onSlideChangeTransitionEnd={(swiper) => {
          if (props.loop) {
            swiper.loopFix();
          }
        }}
        onSlidesUpdated={forceUpdate}
        onLock={forceUpdate}
        onReachBeginning={forceUpdate}
        onReachEnd={forceUpdate}
        onSlideChange={(swiper) => {
          forceUpdate();
          onSlideChange && onSlideChange(swiper);
        }}
      >
        {children}
        {pagination && <PaginationController autoplayControl={autoplay?.control} />}
      </SwiperBase>
      {showNavigationArrows && (
        <>
          <div
            className={`swiper-button-prev ${isLeftDisabled ? 'swiper-button-disabled' : ''}`}
            onClick={() => swiperRef.current.slidePrev()}
          ></div>
          <div
            className={`swiper-button-next ${isRightDisabled ? 'swiper-button-disabled' : ''}`}
            onClick={() => swiperRef.current.slideNext()}
          ></div>
        </>
      )}
    </div>
  );
};

export default Swiper;
